import { LightweightElement } from "@/store/lightweight-elements-slice";
import { locationFilterForReportAndSnapshotTree } from "@/utils/tree-generation/custom-tree-filters";
import { ITreeRenderDecision } from "@/utils/tree-generation/tree-render-filters";
import {
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";

/**
 * Dedicated filter for the snapshot tool. It intentionally hides scan and
 * capture-tree elements so the snapshot only shows rooms and 4D sessions,
 * independently from the report filter.
 *
 * @returns Shows tree of sheets at the top level and the locations being their children.
 *    Also, display the timeseries elements of the locations
 * @param iElement IElement to use as base for the filtering
 * @param parentType Parent type of the given iElement
 * @param videoModeCopyTagId ID of label tagging video mode copies
 * @param customDate Custom date to use instead of the element's createdAt date
 */
export function snapshotFilter(
  iElement: IElement | LightweightElement,
  parentType?: string,
  videoModeCopyTagId?: GUID,
  customDate?: string,
): ITreeRenderDecision {
  if (iElement.type === IElementType.section) {
    const { typeHint } = iElement;

    if (typeHint === IElementTypeHint.area) {
      // Keep the parent of the floor plan pdfs folder
      return {
        shouldKeepElement: true,
        shouldKeepChildren: true,
      };
    }

    if (typeHint === IElementTypeHint.captureTree) {
      // Hide the capture tree and its scans from the snapshot
      return {
        shouldKeepElement: false,
        shouldKeepChildren: false,
      };
    }

    if (typeHint === IElementTypeHint.dataSession) {
      return {
        shouldKeepElement: true,
        shouldKeepChildren: false,
      };
    }
  }

  if (
    iElement.type === IElementType.timeSeries &&
    iElement.typeHint === IElementTypeHint.dataSession &&
    iElement.childrenIds?.length
  ) {
    return {
      shouldKeepElement: true,
      shouldKeepChildren: true,
      displayName: "4D Sessions",
    };
  }

  return locationFilterForReportAndSnapshotTree(
    iElement,
    parentType,
    videoModeCopyTagId,
    customDate,
  );
}
